const ONE_DAY = 1000 * 60 * 60 * 24;
const mappingConfig = {
    cn: {
        tt: '周日 周一 周二 周三 周四 周五 周六',
        ttt: '星期日 星期一 星期二 星期三 星期四 星期五 星期六',
        MM: '01 02 03 04 05 06 07 08 09 10 11 12',
        M: '1 2 3 4 5 6 7 8 9 10 11 12'
    },
    en: {
        tt: 'Sun Mon Tue Wed Thu Fri Sat',
        ttt: 'Sunday Monday Tuesday Wednesday Thursday Friday Saturday',
        MM: 'January February March April May June July August September October November December',
        M: 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'
    }
};
/**
 * 格式化时间，支持的格式:
 *       yyyy ==> 完整的年份，如：2015
 *       yy   ==> 简短的年份，如：15
 *       MM   ==> 月份填充0，如：05
 *       Mm   ==> 月份填充，如: 1月
 *       M    ==> 月份，不填充0，如：5
 *       dd   ==> 日期，填充0，如：08
 *       Dd   ==> 日期，如: 21日
 *       d    ==> 日期，不填充0，如：8
 *       hh   ==> 小时，填充0，如：09
 *       h    ==> 小时，不填充0，如：9
 *       mm   ==> 分钟，填充0，如：28
 *       m    ==> 分钟，不填充0，如：8
 *       ss   ==> 秒，填充0，如：28≈
 *       s    ==> 秒，不填充0，如：8
 *       tt   ==> 星期，如：星期二
 *       t    ==> 星期，如：周二
 * @param date 要格式化的日期／日期字符串
 * @param [pattern='yyyy-MM-dd']   目标格式，比如："yyyy-MM-dd"
 * @param lang
 * @example
 *      dateFormat('2018-08-08', 'yy-M-d');
 *      dateFormat(new Date(), 'yyyy-MM-dd tt')
 */
export function dateFormat(date, pattern = 'yyyy-MM-dd', lang = 'cn') {
    if (!date || !isValidDate(date)) {
        return '';
    }
    const tempDate = toDate(date);
    const year = tempDate.getFullYear();
    const month = tempDate.getMonth();
    const d = tempDate.getDate();
    const day = tempDate.getDay();
    const hours = tempDate.getHours();
    const minutes = tempDate.getMinutes();
    const seconds = tempDate.getSeconds();
    const mapping = mappingConfig[lang];
    return pattern
        .replace(/\byyyy\b/g, String(year))
        .replace(/\bMM\b/g, mapping.MM.split(' ')[month])
        .replace(/\bM\b/g, mapping.M.split(' ')[month])
        .replace(/Mm/g, mapping.M.split(' ')[month] + '月')
        .replace(/\bdd\b/g, String(d + 100).substring(1))
        .replace(/Dd/g, String(d + 100).substring(1) + '日')
        .replace(/\bd\b/g, String(d))
        .replace(/\btt\b/g, mapping.tt.split(' ')[day])
        .replace(/\bttt\b/g, mapping.ttt.split(' ')[day])
        .replace(/\bhh\b/g, String(hours + 100).substring(1))
        .replace(/\bh\b/g, String(hours))
        .replace(/\bmm\b/g, String(minutes + 100).substring(1))
        .replace(/\bh\b/g, String(minutes))
        .replace(/\bss\b/g, String(seconds + 100).substring(1))
        .replace(/\bs\b/g, String(seconds));
}
/**
 * 是否是一个有效的日期
 * @param date
 * @param needConvert
 */
export function isValidDate(date, needConvert = true) {
    // 转换字符串类型时间戳
    const checkDate = needConvert ? convertStrNumberTime(date) : date;
    // 如果日期为空，则认为不合格
    if (!checkDate) {
        return false;
    }
    return new Date(checkDate).toString() !== 'Invalid Date';
}
/**
 * 转换日期 - 日期不合格返回null
 * @param date
 */
export function convertDate(date) {
    // 首先将字符串类型时间戳进行转换
    const checkData = convertStrNumberTime(date) || null;
    if (!isValidDate(checkData, false)) {
        // eslint-disable-next-line no-console
        console.warn(`[convertDate]：Invalid date: ${date}`);
        return null;
    }
    if (typeof checkData === 'string') {
        return new Date(checkData.replace(/-/g, '/'));
    }
    return new Date(checkData);
}
/**
 * 转换字符串类型时间戳
 * @param date
 */
function convertStrNumberTime(date) {
    // 是纯数字字符串时，转为数字
    if (date && typeof date === 'string' && /^[0-9]*$/.test(date)) {
        return parseInt(date, 10);
    }
    return date;
}
/**
 * 计算两个日期间的天数
 * @param startTime - 开始时间
 * @param endTime - 结束时间
 */
export function distanceTime(startTime, endTime) {
    const start = toDate(startTime);
    const end = toDate(endTime);
    const endDate = new Date(end.setHours(0, 0, 0));
    const nowDate = new Date(start.setHours(0, 0, 0));
    const endStamp = endDate.getTime();
    const nowStamp = nowDate.getTime();
    const endDay = endDate.getDate();
    const nowDay = nowDate.getDate();
    const distance = parseFloat(Math.abs((endStamp - nowStamp) / ONE_DAY).toFixed(3));
    if (distance === 0) {
        return 0;
    }
    else if (distance > 0 && distance < 1 && endDay > nowDay) {
        return 1;
    }
    else {
        return Math.ceil(distance);
    }
}
/**
 * 添加日期
 * @param date - 开始日期
 * @param config - 配置 支持年月日添加
 * @param formatConfig - 格式化配置
 */
export function appendDate(date, config, formatConfig) {
    const { year = 0, month = 0, date: iDate = 0 } = config;
    const { format = false, pattern = 'yyyy-MM-dd', lang = 'cn' } = formatConfig || {};
    const newDate = toDate(date);
    const currentYear = newDate.getFullYear();
    const currentMonth = newDate.getMonth();
    const currentDate = newDate.getDate();
    const nextDate = newDate.setFullYear(currentYear + year, currentMonth + month, currentDate + iDate);
    const result = new Date(nextDate);
    return format ? dateFormat(result, pattern, lang) : result;
}
/**
 * 两个日期将个月份数
 * @param start
 * @param end
 */
export function monthDiff(start, end) {
    // 任意一个日期无效或开始大于结束，都返回0
    if (!isValidDate(start) || !isValidDate(end)) {
        return 0;
    }
    const startDate = toDate(start);
    const endDate = toDate(end);
    if (startDate > endDate) {
        return 0;
    }
    let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
    months -= startDate.getMonth();
    months += endDate.getMonth();
    return months <= 0 ? 0 : months;
}
/**
 * 转换日期 - 日期不合格时返回当前日期
 * @param date
 * @param defaultDate
 */
export function toDate(date, defaultDate = new Date()) {
    const result = convertDate(date);
    return result || defaultDate;
}
/**
 * 比较两个日期 - 第一个日期是否小于等于第二个日期
 * @param start
 * @param end
 * @param needEqual
 */
export function compareDate(start, end, needEqual = false) {
    if (!isValidDate(start) || !isValidDate(end)) {
        return false;
    }
    const startTime = toDate(start).getTime();
    const endTime = toDate(end).getTime();
    return needEqual ? endTime >= startTime : endTime > startTime;
}
/**
 * 比较日期 - 在天的维度
 * @param start
 * @param end
 * @param needEqual
 */
export function compareDateWithDay(start, end, needEqual = false) {
    if (!isValidDate(start) || !isValidDate(end)) {
        return false;
    }
    const startTime = toDate(start).setHours(0, 0, 0, 0);
    const endTime = toDate(end).setHours(0, 0, 0, 0);
    return needEqual ? endTime >= startTime : endTime > startTime;
}
/**
 * 日期是否在某个时间范围内
 * @param dirtyDate
 * @param dirtyRange
 */
export function isWithinRange(dirtyDate, dirtyRange) {
    const { start, end } = dirtyRange || {};
    if (!isValidDate(dirtyDate) || !isValidDate(start) || !isValidDate(end)) {
        return false;
    }
    const time = toDate(dirtyDate).getTime();
    const startTime = toDate(start).getTime();
    const endTime = toDate(end).getTime();
    // 如果开始大于结束
    if (startTime > endTime) {
        // eslint-disable-next-line no-console
        console.warn('[isWithinRange]：开始日期大于结束日期，请检查');
        return false;
    }
    return time >= startTime && time <= endTime;
}
/**
 * 是否在某个日期范围内
 * @param dirtyDate
 * @param dirtyRange
 */
export function isWithinDayRange(dirtyDate, dirtyRange) {
    const { start, end } = dirtyRange || {};
    if (!isValidDate(dirtyDate) || !isValidDate(start) || !isValidDate(end)) {
        return false;
    }
    const time = toDate(dirtyDate).setHours(0, 0, 0, 0);
    const startTime = toDate(start).setHours(0, 0, 0, 0);
    const endTime = toDate(end).setHours(0, 0, 0, 0);
    // 如果开始大于结束
    if (startTime > endTime) {
        // eslint-disable-next-line no-console
        console.warn('[isWithinDayRange]：开始日期大于结束日期，请检查');
        return false;
    }
    return time >= startTime && time <= endTime;
}
